import {
  ConfluenceDatabaseCreateCommand,
  ConfluenceDatabaseUpdateCommand,
  type ConfluenceDatabaseUpdateCommandProperties,
  ContentReadyCommand,
  ReadContentCommand,
  ROVO_CHAT_CLIENT_ID,
  RovoStreamAbortedCommand,
  RovoStreamCompletedCommand,
  RovoStreamErroredCommand,
  type ConfluenceCreateDatabaseCommandProperties,
  ContentDestroyCommand
} from '@atlassian/rovo-content-bridge-api-commands';
import { AifcContentCopiedCommand } from '@atlassian/rovo-content-bridge-api-commands/commands/aifc-content-copied-command';

import { isFeatureFlagEnabled } from '@canvas/client-feature-flags';
import { CONFLUENCE_DATABASE_CLIENT_ID } from '@canvas/databases-bridge-api';
import { isRovoPreview } from '@canvas/frame-api/host-api/host-frame-context';
import { client, createRovoBridgeAPIClient } from '@canvas/rovo-content-bridge-api';

import { bridgeEventEmitter } from './bridge-command-emitter';
import { createDatabase } from './handlers/create-database-handler';
import { editDatabase } from './handlers/edit-database-handler';
import { readDatabaseContent } from './handlers/read-database-content-handler';
import {
  handleStreamCompleted,
  handleStreamErrored,
  handleStreamAborted
} from './handlers/stream-lifecycle-handlers';

export const initDatabasesRovoContentBridge = (contentId: string) => {
  const clientId = client(`${CONFLUENCE_DATABASE_CLIENT_ID}-${contentId}`);

  const bridge = createRovoBridgeAPIClient(clientId, (on: any) => {
    on(
      ConfluenceDatabaseCreateCommand,
      (params: ConfluenceCreateDatabaseCommandProperties) =>
        createDatabase(params, contentId)
    );
    on(
      ConfluenceDatabaseUpdateCommand,
      (params: ConfluenceDatabaseUpdateCommandProperties) => {
        editDatabase(params, contentId);
      }
    );
    on(ReadContentCommand, () => readDatabaseContent(contentId));
    on(RovoStreamCompletedCommand, handleStreamCompleted);
    on(RovoStreamErroredCommand, handleStreamErrored);
    on(RovoStreamAbortedCommand, handleStreamAborted);
  });

  bridgeEventEmitter.initBridge(bridge);

  const contentContext: 'staging-area' | 'global' = isRovoPreview()
    ? 'staging-area'
    : 'global';

  bridge.send(ContentReadyCommand, {
    receiverId: ROVO_CHAT_CLIENT_ID,
    senderId: clientId,
    contentId,
    contentType: 'database',
    contentContext,
    canProvideContext: true
  });

  // When running in the AIFC staging area, notify the parent frame whenever the user
  // copies content so it can fire the unified `aiResult actioned` analytics event.
  let handleCopy: (() => void) | undefined;
  if (isRovoPreview() && isFeatureFlagEnabled('aifcCopyAnalytics')) {
    handleCopy = () => {
      try {
        bridge.send(AifcContentCopiedCommand, {
          senderId: clientId,
          receiverId: ROVO_CHAT_CLIENT_ID,
          contentType: 'database',
          contentId
        });
      } catch {
        return; // Analytics are best-effort — silently bail rather than disrupting the copy
      }
    };
    document.addEventListener('copy', handleCopy, true);
  }

  // Ensure we unregister all command handlers when the page is unloaded to prevent
  // memory leaks when navigating
  const cleanupBridge = () => {
    // Also remove the copy listener if it was registered (gate-off: no-op removeEventListener)
    if (handleCopy) {
      document.removeEventListener('copy', handleCopy, true);
    }

    bridge.send(ContentDestroyCommand, {
      receiverId: ROVO_CHAT_CLIENT_ID,
      senderId: clientId,
      contentId,
      contentType: 'database',
      contentContext
    });
    bridge.teardown(clientId);
    bridgeEventEmitter.reset();

    window.removeEventListener('beforeunload', cleanupBridge);
    window.removeEventListener('unload', cleanupBridge);
    window.removeEventListener('pagehide', cleanupBridge);
  };

  window.addEventListener('beforeunload', cleanupBridge, { once: true });
  window.addEventListener('unload', cleanupBridge, { once: true });
  window.addEventListener('pagehide', cleanupBridge, { once: true });

  return { clientId, contentId, bridge, teardown: cleanupBridge };
};
